import { HeightField } from '../heightfield/HeightField';
import { SurfaceRuleRef, SurfaceRuleSetRef, TerrainSurfaceRegionRef } from '../SceneManifest';

const RAD_TO_DEG = 180 / Math.PI;

interface AutoFillResult {
  /** Layer index for the highest-priority matching rule. */
  layerA: number;
  /**
   * Layer index for the rule that would win after crossing the nearest rule
   * threshold (within `feather` meters/degrees). Equal to `layerA` when no
   * threshold is within feather distance.
   */
  layerB: number;
  /**
   * Blend weight 0..255 from A toward B. 0 = fully A; 127 = on the threshold
   * (50/50); approaches 0 as we move into A's interior. Always 0 when no
   * rule-boundary feather applies.
   */
  ruleWeight: number;
}

/** Sentinel stored in the height cache for texels where the heightfield returned null. */
const NO_HEIGHT_SAMPLE = Number.NEGATIVE_INFINITY;

/**
 * Resolve the surface layer for a single texel inside an auto-fill region,
 * including rule-boundary feathering. Reads from per-tile caches built once
 * per [rasterizeTile] call (see [buildTerrainCaches]) — the hot loop has no
 * heightfield calls, no virtual dispatch, no per-texel atan2.
 *
 * Returns `{layerA: defaultLayer, layerB: defaultLayer, ruleWeight: 0}` when
 * no rule matches or the heightfield had nothing to sample at this texel.
 *
 * O(R) per texel for matching, plus a second O(R) pass when feathering (only
 * when within `feather` of a threshold). Hot path — keep tight.
 */
function evaluateAutoFillTexel(
  texelIdx: number,
  compiled: readonly CompiledRule[],
  heightCache: Float32Array,
  slopeGradSq: Float32Array,
  surfaceLayerMap: Map<string, number>,
  defaultLayer: number,
  /**
   * Reference height to subtract before comparing against rule `minHeight` /
   * `maxHeight`. Caller passes `0` for absolute mode and the polygon's lowest
   * vertex height for `polygonMin` mode.
   */
  heightOffset: number,
  /**
   * Caller-owned scratch object the evaluator mutates in place. Avoids per-
   * texel `{layerA, layerB, ruleWeight}` literals — at 15k+ texels per tile,
   * the resulting GC churn was eating into the perf-critical rasterize budget.
   * The same object is returned for caller convenience.
   */
  out: AutoFillResult,
): AutoFillResult {
  const rawHeight = heightCache[texelIdx];
  if (rawHeight === NO_HEIGHT_SAMPLE) {
    out.layerA = defaultLayer;
    out.layerB = defaultLayer;
    out.ruleWeight = 0;
    return out;
  }
  const refHeight = rawHeight - heightOffset;
  const gradSq = slopeGradSq[texelIdx];

  const bestCompiled = pickFirstMatching(compiled, refHeight, gradSq);
  if (!bestCompiled) {
    out.layerA = defaultLayer;
    out.layerB = defaultLayer;
    out.ruleWeight = 0;
    return out;
  }
  const bestRule = bestCompiled.rule;
  const layerA = resolveRuleLayer(bestRule, surfaceLayerMap, defaultLayer);

  // Find the nearest threshold within any rule's feather that would change
  // the winner. Two sources:
  //   (a) Best rule's OWN thresholds — A wins now; if we cross one, A stops
  //       matching and a different rule wins. Blend OUT.
  //   (b) HIGHER-priority rules' approach thresholds — A wins now, but if we
  //       crossed into rule C (with higher priority), C would win. Blend IN.
  // Without (b), feather is asymmetric: e.g. rocks (slope ≥ 20) blends toward
  // grass at slope 22°, but grass has no slope bound → at slope 18° pure
  // grass, no blend toward rocks. Visible seam where slope crosses 20° as
  // texels alternate between "rocks-grass blend" and "pure grass". (b) makes
  // both sides of every threshold feather symmetrically.

  let nearestDist = Infinity;
  let nearestFeather = 0;
  let nudgedH = refHeight;
  let nudgedSlopeDeg = 0;
  // Lazy slopeDeg: sentinel -1 means "not yet computed" (slope is always ≥ 0).
  // Inline guard instead of a closure — closures allocated per texel were
  // ~15k allocations per rasterize, observable as GC pressure.
  let slopeDeg = -1;
  const eps = 1e-3;

  // (a) Best rule's internal thresholds.
  const featherA = bestRule.feather;
  if (featherA != null && featherA > 0) {
    if (bestRule.minHeight != null) {
      const d = refHeight - bestRule.minHeight;
      if (d >= 0 && d < featherA && d < nearestDist) {
        nearestDist = d;
        nearestFeather = featherA;
        nudgedH = bestRule.minHeight - eps;
        if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
        nudgedSlopeDeg = slopeDeg;
      }
    }
    if (bestRule.maxHeight != null) {
      const d = bestRule.maxHeight - refHeight;
      if (d >= 0 && d < featherA && d < nearestDist) {
        nearestDist = d;
        nearestFeather = featherA;
        nudgedH = bestRule.maxHeight + eps;
        if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
        nudgedSlopeDeg = slopeDeg;
      }
    }
    if (bestRule.minSlope != null && gradSq < bestCompiled.minSlopeFeatherInnerTanSq) {
      if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
      const d = slopeDeg - bestRule.minSlope;
      if (d >= 0 && d < featherA && d < nearestDist) {
        nearestDist = d;
        nearestFeather = featherA;
        nudgedH = refHeight;
        nudgedSlopeDeg = bestRule.minSlope - eps;
      }
    }
    if (bestRule.maxSlope != null && gradSq > bestCompiled.maxSlopeFeatherInnerTanSq) {
      if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
      const d = bestRule.maxSlope - slopeDeg;
      if (d >= 0 && d < featherA && d < nearestDist) {
        nearestDist = d;
        nearestFeather = featherA;
        nudgedH = refHeight;
        nudgedSlopeDeg = bestRule.maxSlope + eps;
      }
    }
  }

  // (b) Higher-priority rules approaching from outside. `compiled` is sorted
  // priority-desc, so all entries before `bestCompiled` have higher priority.
  for (let i = 0; i < compiled.length; i++) {
    const C = compiled[i];
    if (C.rule.priority <= bestRule.priority) break;
    const fc = C.rule.feather;
    if (fc == null || fc <= 0) continue;
    // "Approach distance" = the largest gap across the axes where this rule
    // currently fails. We need *every* axis within feather (and the violating
    // axes are what need nudging) for an approach to actually flip the winner.
    let maxAxisGap = 0;
    let approachNudgedH = refHeight;
    let approachNudgedSlope = -1; // computed lazily; only consumed if approachOk
    let approachOk = true;
    if (C.rule.minHeight != null) {
      const gap = C.rule.minHeight - refHeight;
      if (gap > 0) {
        if (gap >= fc) {
          approachOk = false;
        } else {
          if (gap > maxAxisGap) maxAxisGap = gap;
          approachNudgedH = C.rule.minHeight + eps;
        }
      }
    }
    if (approachOk && C.rule.maxHeight != null) {
      const gap = refHeight - C.rule.maxHeight;
      if (gap > 0) {
        if (gap >= fc) {
          approachOk = false;
        } else {
          if (gap > maxAxisGap) maxAxisGap = gap;
          approachNudgedH = C.rule.maxHeight - eps;
        }
      }
    }
    if (approachOk && C.rule.minSlope != null && gradSq < C.minSlopeTanSq) {
      if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
      const gap = C.rule.minSlope - slopeDeg;
      if (gap > 0) {
        if (gap >= fc) {
          approachOk = false;
        } else {
          if (gap > maxAxisGap) maxAxisGap = gap;
          approachNudgedSlope = C.rule.minSlope + eps;
        }
      }
    }
    if (approachOk && C.rule.maxSlope != null && gradSq > C.maxSlopeTanSq) {
      if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
      const gap = slopeDeg - C.rule.maxSlope;
      if (gap > 0) {
        if (gap >= fc) {
          approachOk = false;
        } else {
          if (gap > maxAxisGap) maxAxisGap = gap;
          approachNudgedSlope = C.rule.maxSlope - eps;
        }
      }
    }
    if (approachOk && maxAxisGap > 0 && maxAxisGap < nearestDist) {
      nearestDist = maxAxisGap;
      nearestFeather = fc;
      nudgedH = approachNudgedH;
      // If neither slope axis triggered, slope nudge stays at current slope.
      if (approachNudgedSlope < 0) {
        if (slopeDeg < 0) slopeDeg = Math.atan(Math.sqrt(gradSq)) * RAD_TO_DEG;
        nudgedSlopeDeg = slopeDeg;
      } else {
        nudgedSlopeDeg = approachNudgedSlope;
      }
    }
  }

  if (nearestDist === Infinity || nearestFeather === 0) {
    out.layerA = layerA;
    out.layerB = layerA;
    out.ruleWeight = 0;
    return out;
  }

  // Re-evaluate at the nudged (height, slope) to find B.
  const nudgedTan = Math.tan(nudgedSlopeDeg * DEG_TO_RAD);
  const nudgedGradSq = nudgedTan * nudgedTan;
  const otherCompiled = pickFirstMatching(compiled, nudgedH, nudgedGradSq);
  const layerB = otherCompiled
    ? resolveRuleLayer(otherCompiled.rule, surfaceLayerMap, defaultLayer)
    : defaultLayer;
  if (layerB === layerA) {
    out.layerA = layerA;
    out.layerB = layerA;
    out.ruleWeight = 0;
    return out;
  }
  const t = 1 - nearestDist / nearestFeather;
  out.layerA = layerA;
  out.layerB = layerB;
  out.ruleWeight = Math.round(127 * t);
  return out;
}

/**
 * Returns the first matching rule from `compiled` (which is sorted priority-
 * descending at compile time). First match = highest priority that matches,
 * so the loop can early-exit instead of scanning every rule.
 *
 * Height bounds are checked in their native space. Slope bounds use squared
 * tan space (`gradSq` is the per-texel cached value; thresholds are
 * pre-squared at compile time).
 */
function pickFirstMatching(
  compiled: readonly CompiledRule[],
  refHeight: number,
  gradSq: number,
): CompiledRule | null {
  for (let i = 0; i < compiled.length; i++) {
    const c = compiled[i];
    const r = c.rule;
    if (r.minHeight != null && refHeight < r.minHeight) continue;
    if (r.maxHeight != null && refHeight > r.maxHeight) continue;
    if (gradSq < c.minSlopeTanSq) continue;
    if (gradSq > c.maxSlopeTanSq) continue;
    return c;
  }
  return null;
}

function resolveRuleLayer(
  rule: SurfaceRuleRef,
  surfaceLayerMap: Map<string, number>,
  defaultLayer: number,
): number {
  const layer = surfaceLayerMap.get(rule.surfaceId);
  if (layer === undefined) return defaultLayer;
  return layer < 0 ? 0 : layer > 255 ? 255 : layer;
}

/**
 * Lowest vertex height inside an auto-fill polygon, sampled via the
 * heightfield. Returns 0 if every sample is `null` (heightfield outside its
 * bounds for the whole polygon). Used by `polygonMin` mode to translate rule
 * heights into "meters above local base".
 */
function polygonMinHeight(
  vertices: number[][],
  heightField: HeightField,
): number {
  let min = Infinity;
  for (const v of vertices) {
    const h = heightField.sampleHeight(v[0], v[1]);
    if (h !== null && h < min) min = h;
  }
  return min === Infinity ? 0 : min;
}

interface TerrainCaches {
  /** width * height. Stores [NO_HEIGHT_SAMPLE] sentinel where the heightfield returned null. */
  heightCache: Float32Array;
  /**
   * width * height. Squared gradient magnitude (dx² + dz²) — equivalent to
   * `tan(slope)²`. Rule predicate checks compare against pre-squared
   * `tan(threshold)²` so we skip `sqrt` + `atan2` per texel (the dominant
   * cost of the old "slope in degrees" cache).
   */
  slopeGradSq: Float32Array;
}

/**
 * Pre-compute per-texel height + slope for an entire tile, used by every
 * auto-fill region on the same rasterize call. This is the single biggest
 * perf knob: each call to `heightField.sampleHeightWithNormal` does ~12
 * heightmap reads plus an atan2, and the auto-region inner loop used to do
 * that *per texel per region*. By pre-walking the tile once we drop to
 * 1 cheap `sampleHeight` call per texel; the slope is computed inline from
 * the cache via finite difference (no virtual dispatch).
 *
 * For tiles fully inside the heightfield (the common case) the cost is
 * dominated by `sampleHeight` itself — ~0.5–1 ms for a 125×125 tile. Multiple
 * auto regions on the same tile become near-free (rule predicate scan only).
 */
/**
 * Half-width of the slope central-difference stencil, in texels. ±5 texels at
 * 2 m/texel = 20 m baseline — integrates over ~2-3 heightmap cells so cliffs
 * read uniformly and hillsides average out the bilinear-interp micro-noise
 * that would otherwise paint thin grass slivers across rock.
 *
 * The height cache also samples this many rows/cols of "halo" *outside* the
 * tile so the slope at tile-edge texels has valid neighbours from adjacent
 * terrain instead of falling back to the centre value (which would read as
 * locally flat → visible grass seams at every tile boundary).
 */
const SLOPE_STENCIL = 5;

function buildTerrainCaches(
  width: number,
  height: number,
  worldMinX: number,
  worldMinZ: number,
  cellW: number,
  cellD: number,
  heightField: HeightField,
): TerrainCaches {
  const texelCount = width * height;
  const heightCache = new Float32Array(texelCount);
  const slopeGradSq = new Float32Array(texelCount);

  // Prefer the bilinear-only fast sampler when available. On Iceland-scale
  // heightmaps (cellSize ~8 m), the mesh-aligned `sampleHeight` path does
  // 4 bilinear samples + a triangulated interp per call — overkill for rule
  // evaluation, where bilinear is visually indistinguishable.
  const sample = heightField.sampleHeightFast
    ? heightField.sampleHeightFast.bind(heightField)
    : heightField.sampleHeight.bind(heightField);

  // Pass 1: heights, including a halo of `SLOPE_STENCIL` rows/cols outside
  // the tile so slope at edges sees real neighbouring terrain. Allocating
  // a single padded array is ~14% more memory than the un-padded version
  // (negligible) but eliminates per-neighbour bounds checks in the slope
  // pass and removes the tile-seam grass-strip artifact.
  const paddedW = width + 2 * SLOPE_STENCIL;
  const paddedH = height + 2 * SLOPE_STENCIL;
  const paddedHeights = new Float32Array(paddedW * paddedH);
  for (let row = 0; row < paddedH; row++) {
    const wz = worldMinZ + (row - SLOPE_STENCIL + 0.5) * cellD;
    const rowOff = row * paddedW;
    for (let col = 0; col < paddedW; col++) {
      const wx = worldMinX + (col - SLOPE_STENCIL + 0.5) * cellW;
      const h = sample(wx, wz);
      paddedHeights[rowOff + col] = h !== null ? h : NO_HEIGHT_SAMPLE;
    }
  }

  // Extract the un-padded interior into `heightCache` for the per-texel
  // rule evaluator (which keys off `row * width + col`). One typed-array
  // copy per row — fast.
  for (let row = 0; row < height; row++) {
    const src = (row + SLOPE_STENCIL) * paddedW + SLOPE_STENCIL;
    heightCache.set(paddedHeights.subarray(src, src + width), row * width);
  }

  // Pass 2: squared gradient magnitude via central difference. Reads from
  // the padded array so every texel — including tile edges — has valid
  // neighbours. Missing samples (`NO_HEIGHT_SAMPLE`) degrade to flat.
  // No `sqrt`, no `atan2` — predicate checks compare gradSq against
  // pre-squared `tan(threshold)`.
  const dxScale = 1 / (2 * SLOPE_STENCIL * cellW);
  const dzScale = 1 / (2 * SLOPE_STENCIL * cellD);
  for (let row = 0; row < height; row++) {
    const paddedRow = row + SLOPE_STENCIL;
    const dstOff = row * width;
    for (let col = 0; col < width; col++) {
      const paddedIdx = paddedRow * paddedW + (col + SLOPE_STENCIL);
      const h = paddedHeights[paddedIdx];
      if (h === NO_HEIGHT_SAMPLE) {
        slopeGradSq[dstOff + col] = 0;
        continue;
      }
      const hxLRaw = paddedHeights[paddedIdx - SLOPE_STENCIL];
      const hxRRaw = paddedHeights[paddedIdx + SLOPE_STENCIL];
      const hzDRaw = paddedHeights[paddedIdx - SLOPE_STENCIL * paddedW];
      const hzURaw = paddedHeights[paddedIdx + SLOPE_STENCIL * paddedW];
      const hxL = hxLRaw === NO_HEIGHT_SAMPLE ? h : hxLRaw;
      const hxR = hxRRaw === NO_HEIGHT_SAMPLE ? h : hxRRaw;
      const hzD = hzDRaw === NO_HEIGHT_SAMPLE ? h : hzDRaw;
      const hzU = hzURaw === NO_HEIGHT_SAMPLE ? h : hzURaw;
      const dx = (hxR - hxL) * dxScale;
      const dz = (hzU - hzD) * dzScale;
      slopeGradSq[dstOff + col] = dx * dx + dz * dz;
    }
  }

  return { heightCache, slopeGradSq };
}

/**
 * Map rule slope thresholds (degrees) to pre-squared tan space so per-texel
 * predicate checks can compare against `slopeGradSq` directly. Built once per
 * rule-set use; cached on a WeakMap so repeated lookups are free.
 *
 * Also pre-computes the "feather gate" tan² values used to skip the expensive
 * per-texel `Math.atan` in the feather path. A texel only needs slopeDeg
 * computed when it's within `feather` of an active slope threshold — checking
 * gradSq against pre-computed `tan²(threshold±feather)` is a 1-op gate.
 */
interface CompiledRule {
  rule: SurfaceRuleRef;
  minSlopeTanSq: number; // -Infinity → no lower bound (or treat-as-zero)
  maxSlopeTanSq: number; // Infinity → no upper bound
  /** gradSq below this → outside `feather` of `minSlope` (no feather check needed). */
  minSlopeFeatherInnerTanSq: number;
  /** gradSq above this → outside `feather` of `maxSlope`. */
  maxSlopeFeatherInnerTanSq: number;
}

const compiledRulesCache = new WeakMap<SurfaceRuleSetRef, CompiledRule[]>();
const DEG_TO_RAD = Math.PI / 180;

function tanSq(deg: number): number {
  if (deg <= 0) return 0;
  if (deg >= 90) return Infinity;
  const t = Math.tan(deg * DEG_TO_RAD);
  return t * t;
}

function compileRules(ruleSet: SurfaceRuleSetRef): CompiledRule[] {
  const cached = compiledRulesCache.get(ruleSet);
  if (cached) return cached;
  // Sort priority-descending so a `first match wins` loop body works without
  // tracking a "best so far". Stable sort preserves authoring order on ties.
  const sorted = [...ruleSet.rules].sort((a, b) => b.priority - a.priority);
  const compiled: CompiledRule[] = sorted.map((r) => {
    const feather = r.feather != null && r.feather > 0 ? r.feather : 0;
    const minS = r.minSlope;
    const maxS = r.maxSlope;
    return {
      rule: r,
      minSlopeTanSq: minS == null ? -Infinity : tanSq(minS),
      maxSlopeTanSq: maxS == null ? Infinity : tanSq(maxS),
      // "Inner" boundaries: deeper inside the rule by `feather`. Outside these,
      // no slope-feather applies → no need to compute slopeDeg.
      minSlopeFeatherInnerTanSq: minS == null || feather === 0 ? -Infinity : tanSq(minS + feather),
      maxSlopeFeatherInnerTanSq: maxS == null || feather === 0 ? Infinity : tanSq(maxS - feather),
    };
  });
  compiledRulesCache.set(ruleSet, compiled);
  return compiled;
}

/** World-space AABB of a tile. */
export interface TileBoundsXZ {
  worldMinX: number;
  worldMinZ: number;
  worldMaxX: number;
  worldMaxZ: number;
}

/**
 * Rasterizes a list of polygon surface regions into per-tile index + weight maps.
 *
 * **Two-buffer output** (see {@link RasterizedSurfaceMap}):
 *   - `indices`: RG-packed Uint8Array (2 bytes per texel) — R = layerA, G = layerB.
 *     Uploaded with NEAREST filtering so integer indices stay crisp.
 *   - `weight`: Uint8Array (1 byte per texel) — blend toward B, 0 = fully A, 255 = fully B.
 *     Uploaded with LINEAR filtering so the blend gradient smooths bilinearly
 *     across the texel grid (the visible "blocky boundary" artifact without
 *     LINEAR weight sampling).
 *
 * Algorithm:
 *  1. Fill the buffer with the default surface (R=G=defaultLayer, weight=0).
 *  2. Process regions sorted by priority ascending (painter's algorithm: lower
 *     priority first so higher priority regions paint on top).
 *  3. For each region, compute a 2D polygon SDF per texel centre. Within the
 *     symmetric feather band `[-feather, +feather]`, the weight ramps linearly
 *     from 255 (outside, fully old) to 0 (inside, fully new) with 127 exactly
 *     at the polygon edge.
 *
 * The texel resolution defaults to TEXELS_PER_METER texels per world meter.
 * For a standard 250 m tile at 0.5 tex/m this yields a 128×128 map.
 */
export const TEXELS_PER_METER = 0.5; // 2 m per texel

export interface RasterizedSurfaceMap {
  /** RG packed (2 bytes/texel): R=layerA, G=layerB. NEAREST upload. */
  indices: Uint8Array;
  /** Single-channel weight (1 byte/texel): 0 = fully A, 255 = fully B. LINEAR upload. */
  weight: Uint8Array;
  width: number;
  height: number;
  /**
   * Every surfaceId that ended up contributing to this tile — the default
   * surface (when authored) plus every region whose layer was resolved. Used
   * by `TiledTerrainStreamer` to acquire / release per-surface textures
   * through `SurfaceTextureManager` as tiles enter and leave the streaming ring.
   *
   * The set may include an empty string (the placeholder layer-0 entry); the
   * manager treats that as a no-op since it always maps to layer 0.
   */
  surfaceIds: Set<string>;
}

/** Compute the index map pixel dimensions for a tile. */
export function indexMapSize(
  tileBounds: TileBoundsXZ,
  texelsPerMeter: number = TEXELS_PER_METER,
): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round((tileBounds.worldMaxX - tileBounds.worldMinX) * texelsPerMeter)),
    height: Math.max(1, Math.round((tileBounds.worldMaxZ - tileBounds.worldMinZ) * texelsPerMeter)),
  };
}

export function rasterizeTile(
  tileBounds: TileBoundsXZ,
  regions: TerrainSurfaceRegionRef[],
  surfaceLayerMap: Map<string, number>,
  defaultLayerIndex: number,
  texelsPerMeter: number = TEXELS_PER_METER,
  /**
   * The surfaceId of the default layer (layer 0). When non-null, included in the
   * result's `surfaceIds` so the streamer fetches its textures even on tiles with
   * no region overrides. Pass `null` for the unauthored placeholder slot.
   */
  defaultSurfaceId: string | null = null,
  /**
   * Resolved project-scoped surface rule sets, keyed by id. Auto-fill regions
   * whose `ruleSetId` isn't in this map are skipped (the tile retains the
   * default fill in their footprint). Empty map = no auto-fill regions rendered.
   */
  ruleSets: Map<string, SurfaceRuleSetRef> = new Map(),
  /**
   * Height + slope source for auto-fill rule evaluation. Required when any
   * region has `kind: 'auto'`; ignored otherwise. Pass `null` to skip all
   * auto regions.
   */
  heightField: HeightField | null = null,
  /**
   * Optional global default rule set id. When provided AND resolvable in
   * `ruleSets` AND a `heightField` is available, the initial fill evaluates
   * this rule set per terrain texel instead of the flat `defaultSurfaceId`
   * fill. Falls back to flat fill when any of those are missing.
   */
  defaultRuleSetId: string | null = null,
): RasterizedSurfaceMap {
  const { width, height } = indexMapSize(tileBounds, texelsPerMeter);
  const texelCount = width * height;
  const surfaceIds = new Set<string>();
  if (defaultSurfaceId && defaultSurfaceId.length > 0) {
    surfaceIds.add(defaultSurfaceId);
  }

  // Separate buffers: indices (RG, 2 bytes/texel) + weight (R, 1 byte/texel).
  // Decoupling lets the GL layer set NEAREST on indices (integer correctness)
  // and LINEAR on weight (smooth gradient at boundaries).
  const indices = new Uint8Array(texelCount * 2);
  const weight = new Uint8Array(texelCount);

  // Sort by priority ascending so high-priority regions paint last (painter's algorithm).
  const sorted = [...regions].sort((a, b) => a.priority - b.priority);

  const tileW = tileBounds.worldMaxX - tileBounds.worldMinX;
  const tileD = tileBounds.worldMaxZ - tileBounds.worldMinZ;
  const cellW = tileW / width;
  const cellD = tileD / height;

  // Resolve the optional global default rule set. Falls back to flat fill
  // when the id is missing from `ruleSets` or no heightfield is provided.
  const defaultRuleSet =
    defaultRuleSetId != null && heightField != null
      ? ruleSets.get(defaultRuleSetId) ?? null
      : null;

  // Per-tile height + slope cache. Built once (lazily, only when there's an
  // auto region OR a global default rule set, and we have a heightfield) and
  // shared across every auto path on this tile — initial fill plus polygon
  // regions. Drops `sampleHeightWithNormal` from "every texel × every auto
  // source" to "every texel, once".
  let terrainCaches: TerrainCaches | null = null;
  if (heightField) {
    const needsCache = defaultRuleSet != null || sorted.some((r) => r.kind === 'auto');
    if (needsCache) {
      terrainCaches = buildTerrainCaches(
        width,
        height,
        tileBounds.worldMinX,
        tileBounds.worldMinZ,
        cellW,
        cellD,
        heightField,
      );
    }
  }

  const defLayer = Math.max(0, Math.min(255, defaultLayerIndex));
  // Scratch buffer the evaluator writes into per-texel — see comment on
  // `evaluateAutoFillTexel`'s `out` param. Shared across both the global-
  // default fill and the per-region inner loop below.
  const evalScratch: AutoFillResult = { layerA: 0, layerB: 0, ruleWeight: 0 };
  if (defaultRuleSet != null && terrainCaches != null) {
    // Per-texel global default. Every surface the rule set could pick must be
    // in `surfaceIds` so the streamer pre-acquires their textures.
    for (const rule of defaultRuleSet.rules) {
      if (rule.surfaceId) surfaceIds.add(rule.surfaceId);
    }
    // Compile once outside the texel loop — WeakMap-cached but still a real
    // lookup; 15k calls add up.
    const defaultCompiled = compileRules(defaultRuleSet);
    const defaultHeightCache = terrainCaches.heightCache;
    const defaultSlopeGradSq = terrainCaches.slopeGradSq;
    // `heightReference: 'polygonMin'` doesn't apply to the global default
    // (there's no polygon); treat as absolute regardless of the rule set's
    // own setting. Documented in CLAUDE.md.
    for (let i = 0; i < texelCount; i++) {
      evaluateAutoFillTexel(
        i,
        defaultCompiled,
        defaultHeightCache,
        defaultSlopeGradSq,
        surfaceLayerMap,
        defLayer,
        0,
        evalScratch,
      );
      indices[i * 2 + 0] = evalScratch.layerA;
      indices[i * 2 + 1] = evalScratch.layerB;
      weight[i] = evalScratch.ruleWeight;
    }
  } else {
    // Flat default fill: indices=(default, default). Weight=255 (== "fully B"),
    // which since A=B is visually identical to weight=0 BUT makes bilinear
    // sampling continuous across the outer feather edge:
    //   - Outer-feather texel: (newLayer, default, 255)         → renders default
    //   - Outside texel:        (default,  default, 255)         → renders default
    // Both have weight=255, so the bilinear midpoint stays at 255 even where
    // NEAREST indices flip from (newLayer, default) to (default, default).
    // With weight=0 outside (the previous default), the midpoint dropped to ~127
    // and the (newLayer, default) side rendered as a 50% blend — a visible
    // bright streak at the polygon's outer feather edge.
    for (let i = 0; i < texelCount; i++) {
      indices[i * 2 + 0] = defLayer;
      indices[i * 2 + 1] = defLayer;
      weight[i] = 255;
    }
  }

  if (regions.length === 0) return { indices, weight, width, height, surfaceIds };

  // Per-region scratch buffers — sized to the max polygon vertex count we
  // encounter and reused across rows. Avoids per-row allocations inside the
  // hot loop. Float64 keeps SDF math identical to the legacy implementation.
  let scratchCap = 0;
  let edgeAx = new Float64Array(0);
  let edgeAz = new Float64Array(0);
  let edgeDx = new Float64Array(0);
  let edgeDz = new Float64Array(0);
  let edgeLen2 = new Float64Array(0);
  let edgeAabbMinX = new Float64Array(0);
  let edgeAabbMaxX = new Float64Array(0);
  let edgeAabbMinZ = new Float64Array(0);
  let edgeAabbMaxZ = new Float64Array(0);
  // `edgeAz` doubles as the scanline-crossing endpoint; `edgeBz`/`edgeBx`
  // are the *other* endpoint (genuinely distinct from `edgeAx`/`edgeAz`).
  let edgeBz = new Float64Array(0);
  let edgeBx = new Float64Array(0);
  let crossingsX = new Float64Array(0);
  let nearbyEdges = new Int32Array(0);

  for (const region of sorted) {
    const isAuto = region.kind === 'auto';

    // Per-texel rule set + heightfield for auto regions; ignored for single.
    let autoCompiled: readonly CompiledRule[] | null = null;
    // Reference height subtracted before rule comparison. 0 for absolute mode;
    // polygon's lowest vertex height for polygonMin mode (sampled once per
    // region — ~N heightfield queries vs ~15k texel queries, negligible).
    let autoHeightOffset = 0;
    if (isAuto) {
      const rs = ruleSets.get(region.ruleSetId);
      // `terrainCaches` is null only when no auto region survives the
      // resolver check — every auto-region branch here must have it.
      if (!rs || !heightField || !terrainCaches) continue;
      autoCompiled = compileRules(rs);
      if (rs.heightReference === 'polygonMin') {
        autoHeightOffset = polygonMinHeight(region.vertices, heightField);
      }
      // Conservative surface union: every surface referenced by any rule may
      // appear at any texel inside this region's footprint. The streamer's
      // refcounting depends on this superset to acquire textures in time.
      for (const r of rs.rules) {
        if (r.surfaceId) surfaceIds.add(r.surfaceId);
      }
    }

    // For single regions: precompute the constant layer. For auto: keep -1
    // as a placeholder; the inner loop calls `evaluateAutoFillTexel` per texel.
    let singleLayer = -1;
    if (!isAuto) {
      const layerIndex = surfaceLayerMap.get(region.surfaceId);
      if (layerIndex === undefined) continue;
      surfaceIds.add(region.surfaceId);
      singleLayer = Math.max(0, Math.min(255, layerIndex));
    }

    const poly = region.vertices; // [x, z][]
    const n = poly.length;
    if (n < 3) continue;

    const feather = region.featherDistance;
    const feather2 = feather * feather;

    if (n > scratchCap) {
      scratchCap = n;
      edgeAx = new Float64Array(n);
      edgeAz = new Float64Array(n);
      edgeDx = new Float64Array(n);
      edgeDz = new Float64Array(n);
      edgeLen2 = new Float64Array(n);
      edgeAabbMinX = new Float64Array(n);
      edgeAabbMaxX = new Float64Array(n);
      edgeAabbMinZ = new Float64Array(n);
      edgeAabbMaxZ = new Float64Array(n);
      edgeBz = new Float64Array(n);
      edgeBx = new Float64Array(n);
      crossingsX = new Float64Array(n);
      nearbyEdges = new Int32Array(n);
    }

    // Polygon AABB (no feather expansion here — we apply feather per axis below).
    let polyMinX = poly[0][0];
    let polyMaxX = poly[0][0];
    let polyMinZ = poly[0][1];
    let polyMaxZ = poly[0][1];

    // Precompute edge data: each edge runs from poly[j] (a) to poly[i] (b).
    // SoA layout + Float64Array keeps the hot inner loop branch-free and
    // V8-inlining-friendly.
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const ax = poly[j][0];
      const az = poly[j][1];
      const bx = poly[i][0];
      const bz = poly[i][1];
      edgeAx[i] = ax;
      edgeAz[i] = az;
      edgeBz[i] = bz;
      edgeBx[i] = bx;
      const dx = bx - ax;
      const dz = bz - az;
      edgeDx[i] = dx;
      edgeDz[i] = dz;
      edgeLen2[i] = dx * dx + dz * dz;
      const eMinX = ax < bx ? ax : bx;
      const eMaxX = ax > bx ? ax : bx;
      const eMinZ = az < bz ? az : bz;
      const eMaxZ = az > bz ? az : bz;
      edgeAabbMinX[i] = eMinX;
      edgeAabbMaxX[i] = eMaxX;
      edgeAabbMinZ[i] = eMinZ;
      edgeAabbMaxZ[i] = eMaxZ;
      if (i === 0) {
        polyMinX = eMinX;
        polyMaxX = eMaxX;
        polyMinZ = eMinZ;
        polyMaxZ = eMaxZ;
      } else {
        if (eMinX < polyMinX) polyMinX = eMinX;
        if (eMaxX > polyMaxX) polyMaxX = eMaxX;
        if (eMinZ < polyMinZ) polyMinZ = eMinZ;
        if (eMaxZ > polyMaxZ) polyMaxZ = eMaxZ;
      }
    }

    // Clip rasterization window to AABB+feather, intersected with the tile texel grid.
    const testMinX = polyMinX - feather;
    const testMaxX = polyMaxX + feather;
    const testMinZ = polyMinZ - feather;
    const testMaxZ = polyMaxZ + feather;
    const rowStart = Math.max(
      0,
      Math.floor((testMinZ - tileBounds.worldMinZ) / cellD - 0.5),
    );
    const rowEnd = Math.min(
      height - 1,
      Math.ceil((testMaxZ - tileBounds.worldMinZ) / cellD - 0.5),
    );
    const colStart = Math.max(
      0,
      Math.floor((testMinX - tileBounds.worldMinX) / cellW - 0.5),
    );
    const colEnd = Math.min(
      width - 1,
      Math.ceil((testMaxX - tileBounds.worldMinX) / cellW - 0.5),
    );

    for (let row = rowStart; row <= rowEnd; row++) {
      const wz = tileBounds.worldMinZ + (row + 0.5) * cellD;
      if (wz < testMinZ || wz > testMaxZ) continue;

      // 1. Scanline crossings at this row's z (for inside / outside parity).
      //    Counts edges where one endpoint is above wz and the other below.
      //    Identical semantics to the per-texel ray cast in the legacy code,
      //    but amortised over the row.
      let numCrossings = 0;
      for (let i = 0; i < n; i++) {
        const az = edgeAz[i];
        const bz = edgeBz[i];
        if (az > wz !== bz > wz) {
          crossingsX[numCrossings++] =
            edgeAx[i] + ((wz - az) / (bz - az)) * (edgeBx[i] - edgeAx[i]);
        }
      }
      // Insertion sort — fast for the small N we expect per row (~few).
      for (let a = 1; a < numCrossings; a++) {
        const v = crossingsX[a];
        let b = a - 1;
        while (b >= 0 && crossingsX[b] > v) {
          crossingsX[b + 1] = crossingsX[b];
          b--;
        }
        crossingsX[b + 1] = v;
      }

      // 2. Edges whose Z-AABB (expanded by feather) brackets this row's z —
      //    only these can possibly contribute to a within-feather distance
      //    for any texel in this row.
      let numNearby = 0;
      for (let i = 0; i < n; i++) {
        if (wz >= edgeAabbMinZ[i] - feather && wz <= edgeAabbMaxZ[i] + feather) {
          nearbyEdges[numNearby++] = i;
        }
      }

      // 3. Texel sweep. crossingIdx walks monotonically across the row so
      //    parity flips cost O(1) per texel rather than O(crossings).
      let crossingIdx = 0;
      for (let col = colStart; col <= colEnd; col++) {
        const wx = tileBounds.worldMinX + (col + 0.5) * cellW;
        if (wx < testMinX || wx > testMaxX) continue;

        while (crossingIdx < numCrossings && crossingsX[crossingIdx] < wx) crossingIdx++;
        const inside = (crossingIdx & 1) === 1;

        // 4. Minimum squared distance to any nearby edge whose X-AABB
        //    (expanded by feather) brackets wx. Edges outside that band can
        //    only contribute a distance >= feather, so they can't change the
        //    in-feather decision — they're filtered cheaply.
        let minDist2 = Infinity;
        for (let k = 0; k < numNearby; k++) {
          const i = nearbyEdges[k];
          if (wx < edgeAabbMinX[i] - feather) continue;
          if (wx > edgeAabbMaxX[i] + feather) continue;
          const ax = edgeAx[i];
          const az = edgeAz[i];
          const dx = edgeDx[i];
          const dz = edgeDz[i];
          const len2 = edgeLen2[i];
          let ex: number;
          let ez: number;
          if (len2 < 1e-12) {
            ex = wx - ax;
            ez = wz - az;
          } else {
            let t = ((wx - ax) * dx + (wz - az) * dz) / len2;
            if (t < 0) t = 0;
            else if (t > 1) t = 1;
            ex = wx - (ax + t * dx);
            ez = wz - (az + t * dz);
          }
          const d2 = ex * ex + ez * ez;
          if (d2 < minDist2) minDist2 = d2;
        }

        // 5. Apply ramp. minDist2 is the squared *magnitude* of the SDF;
        //    `inside` carries the sign. We sqrt only when the texel actually
        //    lies inside the feather band — interior and exterior "deep"
        //    texels skip the sqrt entirely.
        const texelIdx = row * width + col;
        const idxOff = texelIdx * 2;

        // For single-kind regions, the layer is a hot-loop constant and B==A,
        // ruleWeight=0 (no rule-boundary blending). For auto, the evaluator
        // does a heightfield sample + rule scan + (optional) feather lookup.
        // The branch is pulled out of the SDF math so the single path stays
        // branch-free.
        let texLayerA: number;
        let texLayerB: number;
        let texRuleWeight: number;
        if (isAuto) {
          evaluateAutoFillTexel(
            texelIdx,
            autoCompiled!,
            terrainCaches!.heightCache,
            terrainCaches!.slopeGradSq,
            surfaceLayerMap,
            defLayer,
            autoHeightOffset,
            evalScratch,
          );
          texLayerA = evalScratch.layerA;
          texLayerB = evalScratch.layerB;
          texRuleWeight = evalScratch.ruleWeight;
        } else {
          texLayerA = singleLayer;
          texLayerB = singleLayer;
          texRuleWeight = 0;
        }

        if (feather > 0) {
          if (minDist2 >= feather2) {
            if (inside) {
              // sdf >= feather → fully inside polygon. Write the auto-fill
              // (A, B, ruleWeight) directly; single regions collapse to A=B.
              indices[idxOff] = texLayerA;
              indices[idxOff + 1] = texLayerB;
              weight[texelIdx] = texRuleWeight;
            }
            // else: sdf <= -feather → unchanged.
            continue;
          }
          // Polygon outer feather band: blend the auto-fill's *A* (rule winner)
          // against whatever was there before (prevA), using the polygon-edge
          // SDF ramp. The rule-boundary feather is intentionally lost in this
          // band — only two slots in RG8+weight; document in spec.
          const sdfMag = Math.sqrt(minDist2);
          const sdf = inside ? sdfMag : -sdfMag;
          const prevA = indices[idxOff];
          const t = (feather - sdf) / (2 * feather);
          const w = Math.round((t < 0 ? 0 : t > 1 ? 1 : t) * 255);
          indices[idxOff] = texLayerA;
          indices[idxOff + 1] = prevA;
          weight[texelIdx] = w;
        } else {
          // feather == 0: hard edge — only paint inside. Rule-boundary feather
          // still applies via (A, B, ruleWeight).
          if (!inside) continue;
          indices[idxOff] = texLayerA;
          indices[idxOff + 1] = texLayerB;
          weight[texelIdx] = texRuleWeight;
        }
      }
    }
  }

  return { indices, weight, width, height, surfaceIds };
}

/** Returns true if the polygon's AABB overlaps the tile bounds (expanded by feather). */
export function regionOverlapsTile(
  region: TerrainSurfaceRegionRef,
  tileBounds: TileBoundsXZ,
): boolean {
  const aabb = regionAabb(region);
  if (!aabb) return false;
  return (
    aabb.maxX >= tileBounds.worldMinX &&
    aabb.minX <= tileBounds.worldMaxX &&
    aabb.maxZ >= tileBounds.worldMinZ &&
    aabb.minZ <= tileBounds.worldMaxZ
  );
}

/** Polygon AABB expanded by featherDistance. Null for empty polygons. */
export interface RegionAabb {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export function regionAabb(region: TerrainSurfaceRegionRef): RegionAabb | null {
  const poly = region.vertices;
  if (poly.length === 0) return null;
  let minX = poly[0][0];
  let maxX = poly[0][0];
  let minZ = poly[0][1];
  let maxZ = poly[0][1];
  for (let v = 1; v < poly.length; v++) {
    const x = poly[v][0];
    const z = poly[v][1];
    if (x < minX) minX = x;
    else if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    else if (z > maxZ) maxZ = z;
  }
  const f = region.featherDistance;
  return { minX: minX - f, maxX: maxX + f, minZ: minZ - f, maxZ: maxZ + f };
}

/** Whether two AABBs overlap on XZ. */
export function aabbOverlapsTile(aabb: RegionAabb, tile: TileBoundsXZ): boolean {
  return (
    aabb.maxX >= tile.worldMinX &&
    aabb.minX <= tile.worldMaxX &&
    aabb.maxZ >= tile.worldMinZ &&
    aabb.minZ <= tile.worldMaxZ
  );
}

/**
 * Semantic equality for two surface regions — true if they would rasterize
 * identically. Used by streamers to detect which regions actually changed
 * between updates and skip tiles those regions can't have affected.
 */
export function regionsEqual(a: TerrainSurfaceRegionRef, b: TerrainSurfaceRegionRef): boolean {
  const aKind = a.kind ?? 'single';
  const bKind = b.kind ?? 'single';
  if (aKind !== bKind) return false;
  if (a.kind === 'auto' && b.kind === 'auto') {
    if (a.ruleSetId !== b.ruleSetId) return false;
  } else if (a.kind !== 'auto' && b.kind !== 'auto') {
    if (a.surfaceId !== b.surfaceId) return false;
  }
  if (a.priority !== b.priority) return false;
  if (a.featherDistance !== b.featherDistance) return false;
  if (a.vertices.length !== b.vertices.length) return false;
  for (let i = 0; i < a.vertices.length; i++) {
    if (a.vertices[i][0] !== b.vertices[i][0]) return false;
    if (a.vertices[i][1] !== b.vertices[i][1]) return false;
  }
  return true;
}

/**
 * Returns the AABBs that changed between two region lists. Used by streamers
 * to identify tiles dirtied by an authoring edit:
 *   - Added region   → new AABB
 *   - Removed region → old AABB (so previously painted texels reset)
 *   - Modified region → BOTH old and new AABB (vertex drag exits the old AABB)
 * Unchanged regions contribute nothing. Returns an empty array when nothing
 * relevant changed.
 */
export function changedRegionAabbs(
  oldRegions: readonly TerrainSurfaceRegionRef[],
  newRegions: readonly TerrainSurfaceRegionRef[],
): RegionAabb[] {
  const oldById = new Map<string, TerrainSurfaceRegionRef>();
  for (const r of oldRegions) oldById.set(r.id, r);
  const dirty: RegionAabb[] = [];
  const seenIds = new Set<string>();
  for (const r of newRegions) {
    seenIds.add(r.id);
    const old = oldById.get(r.id);
    if (!old) {
      const aabb = regionAabb(r);
      if (aabb) dirty.push(aabb);
    } else if (!regionsEqual(old, r)) {
      const oldAabb = regionAabb(old);
      const newAabb = regionAabb(r);
      if (oldAabb) dirty.push(oldAabb);
      if (newAabb) dirty.push(newAabb);
    }
  }
  for (const r of oldRegions) {
    if (seenIds.has(r.id)) continue;
    const aabb = regionAabb(r);
    if (aabb) dirty.push(aabb);
  }
  return dirty;
}
